import React, { useState } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { KeyRound, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const PasswordForm = ({ onDone }) => {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 6) return toast.error('New password must be at least 6 characters');
    if (next !== confirm) return toast.error('New passwords do not match');
    setBusy(true);
    try {
      await api.post('/auth/change-password', { current_password: current, new_password: next });
      toast.success('Password updated — a security alert email was sent to your inbox');
      setCurrent(''); setNext(''); setConfirm('');
      onDone?.();
    } catch (err) { toast.error(err.response?.data?.detail || 'Could not change password'); }
    finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-3">
      <div><Label>Current password</Label><Input type="password" value={current} onChange={(e) => setCurrent(e.target.value)} required data-testid="cp-current-input" /></div>
      <div><Label>New password</Label><Input type="password" value={next} onChange={(e) => setNext(e.target.value)} placeholder="Min 6 characters" required data-testid="cp-new-input" /></div>
      <div><Label>Confirm new password</Label><Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required data-testid="cp-confirm-input" /></div>
      <Button type="submit" className="w-full" disabled={busy} data-testid="cp-submit-button">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Update Password'}
      </Button>
    </form>
  );
};

// Inline card for profile pages
export const ChangePasswordCard = () => (
  <Card data-testid="change-password-card">
    <CardHeader className="pb-3"><CardTitle className="text-base flex items-center gap-2"><KeyRound className="h-4 w-4 text-primary" />Change Password</CardTitle></CardHeader>
    <CardContent><PasswordForm /></CardContent>
  </Card>
);

// Compact dialog for layout headers (Admin / Pump)
export const ChangePasswordDialog = ({ triggerClassName = '' }) => {
  const [open, setOpen] = useState(false);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="icon" className={triggerClassName} title="Change password" aria-label="Change password" data-testid="change-password-trigger">
          <KeyRound className="h-5 w-5" />
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><KeyRound className="h-4 w-4 text-primary" />Change Password</DialogTitle></DialogHeader>
        <PasswordForm onDone={() => setOpen(false)} />
      </DialogContent>
    </Dialog>
  );
};
